/**
 * <pdp-stack-gallery>: drives the thumbnail rail + scroll-position
 * indicator for the independently-scrollable stacked PDP gallery
 * (snippets/pdp-stack-gallery.liquid).
 *
 * Deliberately a plain custom element (no @theme/component base class):
 * the whole <media-gallery> ancestor gets replaced wholesale with
 * freshly-fetched markup on every variant change (see media-gallery.js's
 * #handleProductSelect), so a fresh <pdp-stack-gallery> instance is
 * inserted each time — connectedCallback reruns automatically and there is
 * nothing to hand off from the old instance.
 */
class PdpStackGallery extends HTMLElement {
  connectedCallback() {
    this.scrollEl = this.querySelector('.pdp-stack-gallery__scroll');
    this.items = Array.from(this.querySelectorAll('.pdp-stack-gallery__item'));
    this.thumbs = Array.from(this.querySelectorAll('.pdp-stack-gallery__thumb'));
    this.progressEl = this.querySelector('.pdp-stack-gallery__rail-progress');

    if (!this.scrollEl || this.items.length === 0) return;

    this._onThumbClick = this._onThumbClick.bind(this);
    this._onScroll = this._onScroll.bind(this);
    this._ticking = false;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this._onThumbClick));
    this.scrollEl.addEventListener('scroll', this._onScroll, { passive: true });

    if (this.items.length > 1) {
      this._observer = new IntersectionObserver((entries) => this._onIntersect(entries), {
        root: this.scrollEl,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.items.forEach((item) => this._observer.observe(item));
    }

    this._setActive(0);
    this._updateProgress();
  }

  disconnectedCallback() {
    this._observer?.disconnect();
    this.scrollEl?.removeEventListener('scroll', this._onScroll);
    this.thumbs.forEach((thumb) => thumb.removeEventListener('click', this._onThumbClick));
  }

  /** @param {MouseEvent} event */
  _onThumbClick(event) {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) return;
    const index = Number(target.dataset.index);
    const item = this.items[index];
    if (!item) return;
    item.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /** @param {IntersectionObserverEntry[]} entries */
  _onIntersect(entries) {
    let best = null;
    entries.forEach((entry) => {
      if (entry.intersectionRatio > 0 && (!best || entry.intersectionRatio > best.intersectionRatio)) {
        best = entry;
      }
    });
    if (best) {
      const index = this.items.indexOf(best.target);
      if (index !== -1) this._setActive(index);
    }
  }

  _onScroll() {
    if (this._ticking) return;
    this._ticking = true;
    requestAnimationFrame(() => {
      this._updateProgress();
      this._ticking = false;
    });
  }

  _updateProgress() {
    if (!this.progressEl) return;
    const maxScroll = this.scrollEl.scrollHeight - this.scrollEl.clientHeight;
    const fraction = maxScroll > 0 ? this.scrollEl.scrollTop / maxScroll : 0;
    this.progressEl.style.height = `${Math.min(Math.max(fraction, 0), 1) * 100}%`;
  }

  /** @param {number} index */
  _setActive(index) {
    this.thumbs.forEach((thumb, i) => {
      const isActive = i === index;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('pdp-stack-gallery')) {
  customElements.define('pdp-stack-gallery', PdpStackGallery);
}
